import i18next from "src/i18n/config";
import TimeService from "src/services/timeService/timeService";
import { useSelector } from "src/store";
import { useLazyGetEventsQuery } from "src/store/api/events";
import { selectCurrentWorkspaceId } from "src/store/app";
import { downloadTextFile } from "src/ui/utils/download";

import { selectDevicesByGroupIds } from "@dashboard/devices/store/selectors/devices";
import { Device, EventReport, Group, License } from "@dashboard/devices/types";
import { selectGroups, selectGroupsByIds } from "@dashboard/groups/store/selectors";
import { selectLicensesEntities } from "@dashboard/licenses/store/selectors";
import { renderEventContent } from "@dashboard/shared/components/events";
import { Theme, useTheme } from "@mui/material";

type LicenseEntities = Record<string | number, License | undefined>;
type EventRelations = { devices: Device[]; groups: Group[]; licenses: LicenseEntities };

const eventsExportPageSize = 200;

const csvHeaderRow = [
    i18next.t("pages:overviewPage.cards.eventChart.exportColumn.date"),
    i18next.t("pages:overviewPage.cards.eventChart.exportColumn.time"),
    i18next.t("common:label.event"),
    i18next.t("pages:overviewPage.cards.eventChart.exportColumn.type"),
    i18next.t("pages:overviewPage.cards.eventChart.exportColumn.user"),
    i18next.t("pages:overviewPage.cards.eventChart.exportColumn.device"),
    i18next.t("pages:overviewPage.cards.eventChart.exportColumn.group"),
].join(",");

const escapeCsvCell = (value: string) => (/[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
const findLicenseByDeviceId = (devices: Device[], deviceId: number) => devices.find((d) => d?.id === deviceId);
const findDeviceById = (devices: Device[], deviceId: string) => devices.find((d) => d?.id.toString() === deviceId);
const findGroupByLicenseId = (groups: Group[], id: number) => groups.find((g) => g.licenseIds.includes(id));

function resolveEventRelations(event: EventReport, { devices, groups, licenses }: EventRelations) {
    const { eventTypeName, entityId, groupId } = event;
    const isLicenseEvent = eventTypeName.startsWith("LicenseEvent.");
    const isDeviceEvent = eventTypeName.startsWith("DeviceEvent.");
    const isGroupEvent = eventTypeName.startsWith("GroupEvent.");

    const license = isLicenseEvent ? licenses[parseInt(entityId)] : undefined;

    let device: Device | undefined;

    if (license) {
        device = findLicenseByDeviceId(devices, license.id);
    } else if (isDeviceEvent) {
        device = findDeviceById(devices, entityId);
    }

    let group: Group | undefined;
    if (groupId) {
        group = groups.find((g) => g.id === groupId);
    } else if (isGroupEvent) {
        group = groups.find((g) => g.id.toString() === entityId);
    } else if (license) {
        group = findGroupByLicenseId(groups, license.id);
    } else if (device?.licenseId) {
        group = findGroupByLicenseId(groups, device.licenseId);
    }

    return { license, device, group };
}

function buildCsvRow(event: EventReport, allGroups: Group[], relations: EventRelations, theme: Theme) {
    const { license, device, group } = resolveEventRelations(event, relations);
    const { header: eventTitle } = renderEventContent(event, { license, device, group, allGroups }, theme);

    return [
        TimeService.getLocalDate(event.created, "ISO", "date"),
        TimeService.getLocalDate(event.created, "ISO", "time"),
        eventTitle,
        i18next.t(`pages:overviewPage.cards.eventChart.${event.level}`),
        event.actorTypeName === "User" ? event.actorTitle : "",
        device?.name ?? "",
        group?.name ?? "",
    ]
        .map(escapeCsvCell)
        .join(",");
}

export function buildEventsCsv(events: EventReport[], allGroups: Group[], relations: EventRelations, theme: Theme) {
    const sorted = [...events].sort((a, b) => a.created.localeCompare(b.created));
    const rows = sorted.map((event) => buildCsvRow(event, allGroups, relations, theme));
    return "﻿" + [csvHeaderRow, ...rows].join("\r\n");
}

export function useExportEventsToCsv(groupIds: number[], dayRange: number, fromDate: string) {
    const [triggerGetEvents, { isFetching: isExporting }] = useLazyGetEventsQuery();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const devices = useSelector((state) => selectDevicesByGroupIds(state, groupIds));
    const groups = useSelector((state) => selectGroupsByIds(state, groupIds));
    const licenses = useSelector(selectLicensesEntities);
    const allGroups = useSelector(selectGroups);
    const theme = useTheme();

    const onExport = async () => {
        if (!workspaceId) {
            return;
        }

        const baseParams = {
            workspaceId,
            groupIds,
            createdFrom: TimeService.getStartOfDay(fromDate),
            createdTo: TimeService.getEndOfDay(Date.now()),
            allWorkspaceGroupsLength: allGroups.length,
        };

        const allEvents: EventReport[] = [];
        let pageIndex = 1;

        while (true) {
            // eslint-disable-next-line no-await-in-loop -- sequential pagination: each page depends on the previous count
            const { data } = await triggerGetEvents({ ...baseParams, pageIndex, pageSize: eventsExportPageSize });
            if (!data) {
                break;
            }
            allEvents.push(...data.list);
            if (allEvents.length >= data.count || data.list.length === 0) {
                break;
            }
            pageIndex += 1;
        }

        const csv = buildEventsCsv(allEvents, allGroups, { devices, groups, licenses }, theme);
        downloadTextFile(csv, `event-activity-${fromDate}_last-${dayRange}d.csv`, "text/csv;charset=utf-8");
    };

    return { onExport, isExporting };
}
